import { Avatar, Table, Modal, Space, Tag } from 'antd';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { CloseCircleFilled, CheckCircleFilled } from '@ant-design/icons';
import { getImageUrl, getDataDetail } from 'utils/tools';
import lodashValues from 'lodash/values';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import {
  sumBy,
  every,
  some,
  omit,
  flatten,
  compact,
  orderBy,
  last,
  pickBy,
  flattenDeep,
} from 'lodash';
import { DynamicObjectType } from 'types/commons';
import { formatMoney } from 'utils/formattedString';
import { CONDITION_LEVELS, lotDatePattern } from 'constants/constants';
import { useDispatch } from '@redux';
import { getAllProductVariantByProductId } from '@redux/productVariants/actions';

const getStockColor = (totalStock, data, items, assignedLocationId) => {
  if (data?.quantity > totalStock) return '#FA0000';
  if (assignedLocationId && data?.quantity === totalStock) return '#70C800';

  if (
    !assignedLocationId &&
    items?.length > 1 &&
    !some(data.stocks, (storeStock) =>
      every(
        items,
        (item) =>
          item.stocks.find((e) => e.id === storeStock.id)?.stock >=
          data?.quantity,
      ),
    )
  )
    return `#FFB800`;
  if (data?.quantity < totalStock) return '#70C800';
  // if (data?.stocks?.find(e => e.stock < data?.quantity) > 1) return `#FFB800`;
  return '#000';
};

interface IProductsOrderProps {
  isRejectReturn: boolean;
  fulfillmentInspection: DynamicObjectType[];
  data: DynamicObjectType;
  items: DynamicObjectType[];
  disableStock?: boolean;
  disableReplace?: boolean;
}

function ProductsOrder({
  isRejectReturn,
  fulfillmentInspection,
  data: orderData,
  items,
  disableStock,
}: IProductsOrderProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [currentStockData, setCurrentStockData] = useState(null);
  const stores = useSelector<DynamicObjectType>(
    (state) => state.config.stores,
  ) as DynamicObjectType[];

  const productVariantsData = useSelector<DynamicObjectType>(
    (state) => state.productVariants.data,
  );

  // const [currentVariantSKU, setCurrentVariantSKU] = useState(null);

  useEffect(() => {
    if (items?.length > 0) {
      dispatch(
        getAllProductVariantByProductId({
          data: {
            filters: [
              {
                condition: 'and',
                key: 'id',
                operator: 'is',
                value: flatten(
                  items?.map((e) => [e, ...(e?.product?.comboItems || [])]),
                )?.map(
                  (e: DynamicObjectType) =>
                    e?.product?.variantId || e.variantId,
                ),
              },
            ],
            // join: ['detail||name,langCode'],
            pageSize: 100,
          },
          options: {
            isRefresh: true,
          },
        }),
      );
    }
  }, [items]);

  const products = useMemo(() => {
    return flatten(
      items?.map((e, itemIndex) => {
        const stock = e?.stocks?.reduce(
          (e, key) => ({
            ...e,
            [key.id]: {
              ...key,
              ...(orderData?.orderType === 'TAB_TRY_BUY'
                ? orderData?.location
                : stores?.find((item) => item.id === key.id)),
            },
          }),
          {},
        );

        return [
          {
            ...e,
            productSortingIndex: itemIndex,
            name: e.productName,
            properties: ['white', 'size-4K'],
            quantity: e.quantity,
            price: Number(e.price),
            currency: 'đ',
            thumbnail:
              e?.variant?.thumbnail ||
              e.productThumbnail ||
              e?.propduct?.thumbnail,
            stocks: lodashValues(stock),
            itemIndex,
            isMatchLotDate: e?.variant?.isMatchLotDate,
            ...(e?.product?.comboItems?.length > 0 && {
              isCombo: true,
            }),
            // comboItems: ,
          },
          ...(e?.product?.comboItems?.length > 0
            ? e?.product?.comboItems?.map((comboItem, comboIndex) => ({
                ...comboItem?.product,
                ...comboItem,
                isMatchLotDate: comboItem?.variant?.isMatchLotDate,
                comboIndex,
                itemIndex,
                quantity: +comboItem?.quantity * +e?.quantity,
                discountedPrice: +comboItem.price / (+comboItem?.quantity || 1),
                comboProduct: {
                  ...omit(e, ['comboItems']),
                  productSortingIndex: itemIndex,
                },
                isComboItem: true,
                name: comboItem.productName,
                currency: 'đ',
                thumbnail:
                  comboItem?.variant?.thumbnail ||
                  comboItem.productThumbnail ||
                  comboItem?.propduct?.thumbnail,
                stocks: lodashValues(
                  comboItem?.stocks?.reduce(
                    (obj, key) => ({
                      ...obj,
                      [key.id]: {
                        ...key,
                        ...(orderData?.orderType === 'TAB_TRY_BUY'
                          ? orderData?.location
                          : stores?.find((item) => item.id === key.id)),
                      },
                    }),
                    {},
                  ),
                ),
              })) || []
            : []),
        ];
      }) || [],
    );
  }, [orderData, stores, productVariantsData]);

  const fulfillmentInspectionBySku = fulfillmentInspection?.reduce(
    (e, k) => ({
      ...e,
      [k?.sku]: [...(e[k?.sku] || []), k],
    }),
    {},
  );

  const isHasLotDate = products?.find(
    (e: DynamicObjectType) =>
      typeof (e.isMatchLotDate || e?.variant?.isMatchLotDate) === 'boolean',
  );
  const isHasConditionLevel = fulfillmentInspection?.find(
    (e) => e.condition_level,
  );
  const isHasLostItem = fulfillmentInspection?.find(
    (e) => typeof e.lost_quantity === 'number',
  );
  const isHasDamageQuantity = fulfillmentInspection?.find(
    (e) => typeof e.damage_quantity === 'number',
  );

  const columns = [
    {
      title: 'Product',
      dataIndex: 'id',
      render: (data, record) => (
        <div
          className={`d-flex ${record?.isComboItem && 'ml-[40px]'} items-center`}
        >
          <Avatar
            size="large"
            shape="square"
            style={{ width: 40, height: 40, marginRight: 20 }}
            src={getImageUrl(record?.thumbnail)}
          />
          <div>
            <Space size={4}>
              {record?.isGift && <Tag color="red">Gift</Tag>}
              <span>{`${record?.isCombo ? 'Combo: ' : ''}${
                getDataDetail(
                  record,
                  'detail',
                  'name',
                  record?.productName || record?.variant?.slug,
                )?.name
              }`}</span>
              {record?.isComboItem && (
                <Tag color="geekblue" style={{ whiteSpace: 'break-spaces' }}>
                  {`Combo: ${getDataDetail(record?.comboProduct)?.name || getDataDetail(record?.comboProduct?.product)?.name}`}
                </Tag>
              )}
              {record?.bundle && (
                <Tag color="geekblue" style={{ whiteSpace: 'break-spaces' }}>
                  {`Bundle: ${getDataDetail(record?.bundle)?.name}`}
                </Tag>
              )}
            </Space>
            <div className="text-secondary text-sm">
              {record?.variant?.attributeValues?.length > 0
                ? record?.variant?.attributeValues
                    ?.map((item) => getDataDetail(item)?.name)
                    ?.join('/')
                : record?.variantText}
            </div>
            <div className="description">{`SKU: ${
              record?.variantSku || record?.variant?.sku
            }`}</div>
          </div>
        </div>
      ),
    },
    {
      dataIndex: 'quantity',
      render: (data) => <div className="text-right">{data}</div>,
      title: (
        <div className="text-right fw-600">{t('orderDetail.quantity')}</div>
      ),
    },
    // ...(orderData?.statusOms === 'PENDING_PROCESSING' && !disableStock
    ...(!disableStock && orderData?.statusOms === 'AWAITING_CONFIRMATION'
      ? [
          {
            dataIndex: 'stocks',
            render: (data, record) => {
              // const assignedLocation = data?.find(
              //   (e) => e.id === orderData?.locationId,
              // );
              // const totalStock = assignedLocation
              //   ? assignedLocation?.stock
              //   : sumBy(
              //       data?.filter((e) => e?.stock > 0),
              //       'stock',
              //     );
              const totalStock = record?.variant?.stock || 0;
              return record?.isCombo ? (
                <div className="text-right">-</div>
              ) : (
                <div
                  onClick={() => setCurrentStockData(record)}
                  style={{
                    color: getStockColor(
                      totalStock,
                      record,
                      products?.filter((e: DynamicObjectType) => !e.isCombo),
                      orderData?.locationId,
                    ),
                  }}
                  className="text-right pointer"
                >
                  {totalStock}
                  {/* {assignedLocation
                    ? ` (${orderData?.locationId})`
                    : every(data, (e) => e.stock < record?.quantity) &&
                      record?.quantity <= totalStock &&
                      `(!)`} */}
                </div>
              );
            },
            title: (
              <div className="text-right fw-600">
                {t('orderDetail.availStock')}
              </div>
            ),
          },
        ]
      : []),
    ...(isHasLostItem
      ? [
          {
            dataIndex: 'lostQuantity',
            render: (e, item) => {
              return (
                <div className="text-right">
                  {!item?.isCombo ? e ?? 0 : '-'}
                </div>
              );
            },
            width: 120,
            title: (
              <div className="text-right fw-600">
                {t('orderDetail.lostQuantity')}
              </div>
            ),
          },
        ]
      : []),
    ...(isHasDamageQuantity
      ? [
          {
            dataIndex: 'damageQuantity',
            render: (e, item) => {
              return (
                <div className="text-right">
                  {!item?.isCombo ? e ?? 0 : '-'}
                </div>
              );
            },
            width: 120,
            title: (
              <div className="text-right fw-600">
                {t('orderDetail.damageQuantity')}
              </div>
            ),
          },
        ]
      : []),
    ...(isHasConditionLevel || isHasLostItem || isHasDamageQuantity
      ? []
      : [
          {
            dataIndex: 'originalPrice',
            render: (e, item) => {
              return (
                <div
                  style={{ whiteSpace: 'nowrap' }}
                  className="text-right description"
                >
                  {formatMoney(Number(item?.variant?.originalPrice))}
                </div>
              );
            },
            width: 120,
            title: (
              <div className="text-right fw-600">
                {t('orderDetail.originalPrice')}
              </div>
            ),
          },
          {
            dataIndex: 'discountedPrice',
            render: (e, item) => {
              return item?.isComboItem ? (
                <div style={{ whiteSpace: 'nowrap' }} className="text-right">
                  {formatMoney(+item?.price)}
                </div>
              ) : (
                <div style={{ whiteSpace: 'nowrap' }} className="text-right">
                  {formatMoney(
                    item?.isGift
                      ? 0
                      : orderData?.channel === 'tiktokshops'
                        ? Number(item?.variant?.originalPrice) -
                          Number(item?.lineItemMarketPlace?.seller_discount)
                        : item?.price,
                  )}
                </div>
              );
            },
            width: 120,
            title: (
              <div className="text-right fw-600">
                {t('orderDetail.discountedPrice')}
              </div>
            ),
          },
          {
            dataIndex: 'discountedAmount',
            render: (e, item) => {
              return item?.isComboItem ? (
                <div className="text-right">-</div>
              ) : (
                <div style={{ whiteSpace: 'nowrap' }} className="text-right">
                  {formatMoney(
                    item?.isGift
                      ? item?.priceBeforeDiscount
                      : orderData?.channel === 'tiktokshops'
                        ? Number(item?.lineItemMarketPlace?.seller_discount)
                        : Number(item?.variant?.originalPrice) -
                          Number(item?.price),
                  )}
                </div>
              );
            },
            // <div style={{ whiteSpace: 'nowrap' }} className="text-right">{formatMoney(((Number(item?.price) * quantity) / Number(data?.subTotal) * Number(data?.discount)))}</div>
            width: 120,
            title: (
              <div className="text-right fw-600">
                {t('orderDetail.discountedAmount')}
              </div>
            ),
          },
          {
            dataIndex: 'finalPrice',
            render: (e, item) => {
              const finalPrice =
                Number(
                  orderData?.channel === 'tiktokshops'
                    ? Number(item?.variant?.originalPrice) -
                        Number(item?.lineItemMarketPlace?.seller_discount)
                    : item?.price,
                ) * item?.quantity;
              // orderData?.channel === 'shopee'
              //   ? Number(item?.variant?.originalPrice) -
              //     Number(item?.variant?.originalPrice) +
              //     Number(item?.price) -
              //     Number(item?.allocateDiscount)
              //   : Number(item?.price) * item?.quantity;
              return item?.isComboItem ? (
                <div className="text-right">-</div>
              ) : (
                <div style={{ whiteSpace: 'nowrap' }} className="text-right">
                  {formatMoney(item?.isGift ? 0 : finalPrice)}
                </div>
              );
            },
            width: 120,
            title: (
              <div className="text-right fw-600">
                {t('orderDetail.finalPrice')}
              </div>
            ),
          },
        ]),
    ...(isHasConditionLevel
      ? [
          {
            dataIndex: 'conditionLevels',
            render: (conditionLevels, item) => {
              return conditionLevels?.length > 0 ? (
                <div className="text-right">
                  {conditionLevels?.map((e, i) => (
                    <>
                      {i > 0 && ', '}
                      {`${e?.quantity} con `}
                      <span
                        key={e?.value}
                        style={{
                          color: e?.color,
                        }}
                      >
                        {e?.value}
                      </span>
                    </>
                  ))}
                </div>
              ) : (
                <div className="text-right">{item?.conditionLevel || '-'}</div>
              );
            },
            width: 120,
            title: (
              <div className="text-right fw-600">
                {t('orderDetail.conditionLevel')}
              </div>
            ),
          },
          {
            dataIndex: 'messages',
            render: (messages) => {
              return messages?.length > 0 ? (
                <ul style={{ listStyle: 'none' }}>
                  {messages?.map((e) => <li key={e}>{`-${e}`}</li>)}
                </ul>
              ) : (
                <div className="text-right">-</div>
              );
            },
            width: 120,
            title: (
              <div className="text-right fw-600">
                {t('orderDetail.message')}
              </div>
            ),
          },
        ]
      : []),
    ...(isHasLotDate
      ? [
          {
            dataIndex: 'isMatchLotDate',
            render: (e) => {
              return typeof e === 'boolean' ? (
                <div className="text-right">
                  {e ? (
                    <CheckCircleFilled
                      style={{ fontSize: '20px', color: 'green' }}
                    />
                  ) : (
                    <CloseCircleFilled
                      style={{ fontSize: '20px', color: 'red' }}
                    />
                  )}
                </div>
              ) : (
                <div className="text-right">-</div>
              );
            },
            width: 120,
            title: (
              <div className="text-right fw-600">
                {t('orderDetail.matchLotDate')}
              </div>
            ),
          },
        ]
      : []),
  ];

  const variantsWithInspection =
    fulfillmentInspection?.length > 0
      ? pickBy(
          products
            ?.map((e: DynamicObjectType, i) => ({
              ...e,
              sortingIndex: i,
            }))
            ?.reduce((obj, item: DynamicObjectType) => {
              const lotDate: string = last(
                (item?.variantSku || item?.variant?.sku)?.split('_'),
              );
              const isHasLotDate = lotDatePattern.test(lotDate);
              const sku = isHasLotDate
                ? (item?.variantSku || item?.variant?.sku)?.replace(
                    `_${lotDate}`,
                    '',
                  )
                : item?.variantSku || item?.variant?.sku;
              const variantSku = `${sku}_${item?.comboProduct?.variantSku || item?.comboProduct?.variant?.sku || ''}`;

              return {
                ...obj,
                [variantSku]: {
                  key: variantSku,
                  variantSku: sku,
                  quantity:
                    (item?.quantity || 0) + (obj?.[variantSku]?.quantity || 0),
                },
              };
            }, {}),
          (variantItem: DynamicObjectType) =>
            Object.keys(fulfillmentInspectionBySku || {}).find(
              (e) => e === variantItem?.variantSku,
            ),
        )
      : null;

  const fulfillmentInspectionWithSku = compact(
    flattenDeep(
      Object.values(fulfillmentInspectionBySku || {})?.map(
        (inputArray: DynamicObjectType[]) => {
          let countIndex = 0;
          let countChunk = 0;
          const result = inputArray.reduce((resultArray, item, index) => {
            if (
              countIndex ===
              Object.values(
                pickBy(
                  variantsWithInspection,
                  (e) => e.variantSku === item?.sku,
                ) || {},
              )?.[countChunk]?.quantity
            ) {
              countChunk += 1;
              countIndex = 0;
            }
            const perChunk = Object.values(
              pickBy(
                variantsWithInspection,
                (e) => e.variantSku === item?.sku,
              ) || {},
            )?.[countChunk]?.quantity;
            const chunkIndex = Math.floor(index / perChunk);

            if (!resultArray[chunkIndex]) {
              // eslint-disable-next-line
              resultArray[chunkIndex] = []; // start a new chunk
            }

            resultArray[chunkIndex].push({
              ...item,
              countChunk,
              key: Object.values(
                pickBy(
                  variantsWithInspection,
                  (e) => e.variantSku === item?.sku,
                ) || {},
              )?.[countChunk]?.key,
            });
            countIndex += 1;
            return resultArray;
          }, []);
          return result;
        },
      ),
    ),
  )?.reduce(
    (obj, item) => ({
      ...obj,
      [item?.key]: [...(obj?.[item?.key] || []), item],
    }),
    {},
  );

  const returnProducts = orderBy(
    fulfillmentInspection?.length > 0 &&
      Object.values(
        products
          ?.map((e: DynamicObjectType, i) => ({
            ...e,
            sortingIndex: i,
          }))
          ?.reduce((obj, item: DynamicObjectType) => {
            const lotDate: string = last(
              (item?.variantSku || item?.variant?.sku)?.split('_'),
            );
            const isHasLotDate = lotDatePattern.test(lotDate);
            const sku = isHasLotDate
              ? (item?.variantSku || item?.variant?.sku)?.replace(
                  `_${lotDate}`,
                  '',
                )
              : item?.variantSku || item?.variant?.sku;
            const variantSku = `${sku}_${item?.comboProduct?.variantSku || item?.comboProduct?.variant?.sku || ''}`;

            return {
              ...obj,
              [variantSku]: {
                ...item,
                formattedVariantSku: variantSku,
                quantity:
                  (item?.quantity || 0) + (obj?.[variantSku]?.quantity || 0),
                lostQuantity: sumBy(
                  fulfillmentInspectionWithSku?.[variantSku],
                  'lost_quantity',
                ),
                damageQuantity: sumBy(
                  fulfillmentInspectionWithSku?.[variantSku],
                  'damage_quantity',
                ),
                conditionLevels: Object.values(
                  fulfillmentInspectionWithSku?.[variantSku]?.reduce(
                    (e, k) => ({
                      ...e,
                      [k?.condition_level]: {
                        value: k?.condition_level,
                        color: CONDITION_LEVELS[k?.condition_level]?.color,
                        quantity: 1 + (e?.[k?.condition_level]?.quantity || 0),
                      },
                    }),
                    {},
                  ) || {},
                ),
                messages: compact(
                  fulfillmentInspectionWithSku?.[variantSku]?.map(
                    (e) => e?.description,
                  ),
                ),
                ...(isHasLotDate && {
                  isMatchLotDate:
                    item?.isMatchLotDate &&
                    (typeof obj?.[variantSku]?.isMatchLotDate !== 'boolean' ||
                      obj?.[variantSku]?.isMatchLotDate),
                }),
              },
            };
          }, {}),
      ),
    'sortingIndex',
  );

  return (
    <Wrapper className="w-100">
      <Modal
        title="Inventory check"
        open={!!currentStockData}
        footer={null}
        onCancel={() => setCurrentStockData(null)}
      >
        <div className="flex-center-between">
          <div className="d-flex">
            <Avatar
              size="large"
              shape="square"
              style={{ width: 40, height: 40, minWidth: 40, marginRight: 20 }}
              src={getImageUrl(currentStockData?.thumbnail)}
            />
            <div>
              <div>
                {
                  getDataDetail(
                    currentStockData,
                    'detail',
                    'name',
                    currentStockData?.variant?.slug,
                  )?.name
                }
              </div>
              <div className="description">{`SKU: ${
                currentStockData?.variantSku || currentStockData?.variant?.sku
              }`}</div>
            </div>
          </div>
          <div style={{ fontWeight: 500 }} className="text-right">
            <div>{t('orderDetail.quantity')}</div>
            <div>{currentStockData?.quantity}</div>
          </div>
        </div>
        <br />
        <Table
          scroll={{ x: 1024 }}
          pagination={false}
          dataSource={currentStockData?.stocks}
        >
          <Table.Column
            render={(data, row: DynamicObjectType) => (
              <div>{`${row?.name ? `${row?.name} | ` : ''}${row?.id}`}</div>
            )}
            dataIndex="name"
            title={
              <div className="text-left fw-600">
                {t('orderDetail.storeName')}
              </div>
            }
          />
          <Table.Column
            dataIndex="stock"
            render={(data) => <div className="text-right">{data}</div>}
            title={
              <div className="text-right fw-600">{t('orderDetail.stock')}</div>
            }
          />
        </Table>
        <br />
      </Modal>
      <div className="order-info-box__content">
        <Table
          pagination={false}
          dataSource={
            isRejectReturn &&
            (isHasConditionLevel || isHasLostItem || isHasDamageQuantity)
              ? returnProducts
              : products
          }
          columns={columns}
          scroll={{ x: 1024 }}
          rowClassName={(record) => record?.isComboItem && 'combo-item-row'}
        />
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  .combo-item-row {
    td {
      background: #fafafa !important;
    }
  }
  .ant-avatar {
    min-width: 40px;
  }
`;

ProductsOrder.propTypes = {
  data: PropTypes.object,
};

export default ProductsOrder;
